import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Radio, Play, Pause, Volume2, VolumeX, Music2 } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';

const OurStations = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const audioRef = useRef(null);
  const [currentStation, setCurrentStation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [nowPlaying, setNowPlaying] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  // SEO Content
  const seoTitle = language === 'el' 
    ? 'Οι Σταθμοί μας | Radio Solutions - Ακούστε Live Ραδιόφωνο'
    : 'Our Stations | Radio Solutions - Listen Live Radio';
  
  const seoDescription = language === 'el'
    ? 'Ακούστε live τους ραδιοφωνικούς σταθμούς της Radio Solutions. Greeks Radio, Hits Radio, Hit Classic, Eclectic και The Mix. Ελληνική και διεθνής μουσική 24/7.'
    : 'Listen live to Radio Solutions radio stations. Greeks Radio, Hits Radio, Hit Classic, Eclectic and The Mix. Greek and international music 24/7.';
  
  const seoKeywords = language === 'el'
    ? 'ραδιοφωνικοί σταθμοί, live radio, ελληνικό ράδιο, Greeks Radio, Hits Radio, Hit Classic, Eclectic, The Mix, online radio, streaming radio, ραδιόφωνο online, top songs, best music, top 20, top 40, top 100, today best songs, classic hits, 80s hits, 80s best songs, 80s top 20, 90s hits, 90s best songs, 90s top 20, best radio, radio playlist, ελληνική μουσική, διεθνής μουσική, 24/7 radio, internet radio, web radio, μουσική online'
    : 'radio stations, live radio, greek radio, Greeks Radio, Hits Radio, Hit Classic, Eclectic, The Mix, online radio, streaming radio, top songs, best music, top 20, top 40, top 100, today best songs, classic hits, 80s hits, 80s best songs, 80s top 20, 90s hits, 90s best songs, 90s top 20, best radio, radio playlist, greek music, international music, 24/7 radio, internet radio, web radio';
  
  // Streaming URLs για κάθε σταθμό
  const stations = [
    {
      id: 1,
      name: "GREEKS RADIO",
      tagline: "All the Greek Hits",
      bgGradient: "from-blue-600 to-blue-800",
      accentColor: "blue",
      icon: "🇬🇷",
      description: "Όλες οι ελληνικές επιτυχίες",
      streamUrl: "https://solid9.streamupsolutions.com/proxy/zvbgdllc?mp=/stream"
    },
    {
      id: 2,
      name: "HITS RADIO",
      tagline: "Today's Best Music",
      bgGradient: "from-red-600 to-red-800",
      accentColor: "red",
      icon: "🎵",
      description: "Η καλύτερη μουσική του σήμερα",
      streamUrl: "https://solid2.streamupsolutions.com/proxy/lposuiem?mp=/stream"
    },
    {
      id: 3,
      name: "HIT CLASSIC",
      tagline: "80s - 90s - 2Ks",
      bgGradient: "from-yellow-500 to-orange-600",
      accentColor: "yellow",
      icon: "🎤",
      description: "Κλασικές επιτυχίες από 3 δεκαετίες",
      streamUrl: "https://solid67.streamupsolutions.com/proxy/smzyynlm?mp=/stream"
    },
    {
      id: 4,
      name: "ECLECTIC",
      tagline: "Cosmopolitan • Soul • Jazz • Indie • Electronic",
      bgGradient: "from-gray-800 to-black",
      accentColor: "gray",
      icon: "🎸",
      description: "Cosmopolitan, Soul, Jazz, Indie & Electronic μουσική",
      streamUrl: "https://netradio.live24.gr/bootleg"
    },
    {
      id: 5,
      name: "THE MIX",
      tagline: "The Biggest Hits & Throwbacks",
      bgGradient: "from-purple-600 to-pink-600",
      accentColor: "purple",
      icon: "🎧",
      description: "Τα μεγαλύτερα hits όλων των εποχών",
      streamUrl: "https://newstream.live24.gr/themix"
    }
  ];

  // Fetch metadata για τον τρέχοντα σταθμό
  const fetchMetadata = async (stationId) => {
    try {
      const station = stations.find(s => s.id === stationId);
      if (!station) return;
      
      // Fetch metadata from backend proxy
      const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${API_URL}/api/radio/metadata?stream_url=${encodeURIComponent(station.streamUrl)}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.title || data.artist) {
          setNowPlaying(prev => ({
            ...prev,
            [stationId]: {
              title: data.title || 'Unknown',
              artist: data.artist || '',
              fullText: data.artist ? `${data.artist} - ${data.title}` : data.title
            }
          }));
        }
      }
    } catch (error) {
      console.log('Metadata fetch error:', error);
    }
  };

  // Polling για metadata κάθε 15 δευτερόλεπτα
  useEffect(() => {
    let intervalId;
    if (currentStation && isPlaying) {
      fetchMetadata(currentStation);
      intervalId = setInterval(() => fetchMetadata(currentStation), 15000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [currentStation, isPlaying]);

  // Handle play/pause
  const handlePlayPause = (station) => {
    if (currentStation === station.id && isPlaying) {
      // Pause current station
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      // Play new station
      setIsLoading(true);
      setCurrentStation(station.id);
      
      if (audioRef.current) {
        audioRef.current.src = station.streamUrl;
        audioRef.current.volume = volume;
        audioRef.current.play()
          .then(() => {
            setIsPlaying(true);
            setIsLoading(false);
            fetchMetadata(station.id);
          })
          .catch((error) => {
            console.error('Play error:', error);
            setIsLoading(false);
            setIsPlaying(false);
          });
      }
    }
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else {
      setIsMuted(false);
    }
  };

  // Handle mute toggle
  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume || 0.7;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A0730] via-[#2D0C4B] to-[#1A0730] py-4 sm:py-8">
      {/* SEO Meta Tags - Title inherited from index.html (Radio Solutions Apps) */}
      <Helmet>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <link rel="canonical" href="https://radio-apps.online/our-stations" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://radio-apps.online/our-stations" />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_deploy-lifeline/artifacts/b4jcqxjk_logo_transparent.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Radio Solutions Apps" />
        <meta property="og:locale" content={language === 'el' ? 'el_GR' : 'en_US'} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://radio-apps.online/our-stations" />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_deploy-lifeline/artifacts/b4jcqxjk_logo_transparent.png" />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Radio Solutions Greece" />
        <meta name="geo.region" content="GR" />
        <meta name="geo.country" content="Greece" />
        
        {/* Structured Data - Radio Stations */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Radio Solutions - Our Stations",
            "description": seoDescription,
            "url": "https://radio-apps.online/our-stations",
            "publisher": {
              "@type": "Organization",
              "name": "Radio Solutions Greece",
              "url": "https://radio-apps.online"
            },
            "mainEntity": {
              "@type": "ItemList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "item": {
                    "@type": "RadioStation",
                    "name": "Greeks Radio",
                    "description": "All the Greek Hits - Όλες οι ελληνικές επιτυχίες"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "item": {
                    "@type": "RadioStation",
                    "name": "Hits Radio",
                    "description": "Today's Best Music"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "item": {
                    "@type": "RadioStation",
                    "name": "Hit Classic",
                    "description": "80s - 90s - 2Ks Classic Hits"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 4,
                  "item": {
                    "@type": "RadioStation",
                    "name": "Eclectic",
                    "description": "Cosmopolitan, Soul, Jazz, Indie & Electronic"
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 5,
                  "item": {
                    "@type": "RadioStation",
                    "name": "The Mix",
                    "description": "The Biggest Hits & Throwbacks"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>
      
      {/* Hidden Audio Element */}
      <audio ref={audioRef} preload="none" />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Back Button and Language Switcher - Compact */}
        <div className="mb-2 sm:mb-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-1 px-2 sm:px-4 py-1.5 sm:py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg shadow-sm hover:bg-white/20 hover:shadow-md transition-all duration-200 text-white font-medium text-xs sm:text-base"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Επιστροφή</span>
          </button>
          <LanguageSwitcher />
        </div>

        {/* Header - Compact */}
        <div className="text-center mb-2 sm:mb-6">
          <h1 className="text-lg sm:text-3xl lg:text-4xl font-bold text-white mb-0.5 sm:mb-2 flex items-center justify-center gap-1.5 sm:gap-2">
            <Radio className="w-5 h-5 sm:w-10 sm:h-10 text-purple-400" />
            Οι Σταθμοί μας
          </h1>
          <p className="text-purple-200/80 text-xs sm:text-base max-w-2xl mx-auto px-4 hidden sm:block">
            Demo stations με διάφορα music formats για δημιουργία playlists σε ραδιοφωνικούς σταθμούς και In-Store επιχειρήσεις.
          </p>
        </div>

        {/* Now Playing Bar - Fixed on mobile when playing */}
        {currentStation && isPlaying && (
          <div className="fixed bottom-0 left-0 right-0 sm:relative sm:mb-6 z-50">
            <div className="bg-gradient-to-r from-purple-900/95 to-pink-900/95 backdrop-blur-lg border-t sm:border sm:rounded-2xl border-white/20 p-3 sm:p-4 shadow-2xl">
              <div className="max-w-7xl mx-auto flex items-center gap-3 sm:gap-4">
                {/* Station Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-white font-bold text-sm sm:text-base truncate">
                      {stations.find(s => s.id === currentStation)?.name}
                    </span>
                  </div>
                  {nowPlaying[currentStation] && (
                    <p className="text-purple-200 text-xs sm:text-sm truncate mt-1">
                      <Music2 className="w-3 h-3 inline mr-1" />
                      {nowPlaying[currentStation].fullText || 'Loading...'}
                    </p>
                  )}
                </div>
                
                {/* Volume Control - Hidden on mobile */}
                <div className="hidden sm:flex items-center gap-2">
                  <button onClick={toggleMute} className="text-white/80 hover:text-white">
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-20 sm:w-24 accent-purple-500"
                  />
                </div>
                
                {/* Play/Pause Button */}
                <button
                  onClick={() => handlePlayPause(stations.find(s => s.id === currentStation))}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  ) : (
                    <Play className="w-5 h-5 sm:w-6 sm:h-6 text-white fill-white" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Station Cards Grid - Compact 5 in a row */}
        <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-4 ${currentStation && isPlaying ? 'mb-20 sm:mb-8' : 'mb-8'}`}>
          {stations.map((station) => {
            const isActive = currentStation === station.id;
            const isStationPlaying = isActive && isPlaying;
            
            return (
              <div
                key={station.id}
                className={`
                  relative group overflow-hidden
                  bg-gradient-to-br ${station.bgGradient}
                  rounded-lg sm:rounded-xl shadow-lg
                  transform transition-all duration-300
                  border ${isActive ? 'border-white/50 scale-[1.02]' : 'border-white/10 hover:border-white/30'}
                  ${isStationPlaying ? 'ring-2 ring-white/30 ring-offset-1 ring-offset-purple-900' : ''}
                `}
                data-testid={`station-card-${station.id}`}
              >
                {/* Playing indicator */}
                {isStationPlaying && (
                  <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-1.5 sm:px-2 py-0.5 sm:py-1">
                    <div className="flex gap-0.5">
                      <div className="w-0.5 sm:w-1 h-2 sm:h-3 bg-white rounded-full animate-pulse" style={{animationDelay: '0ms'}} />
                      <div className="w-0.5 sm:w-1 h-3 sm:h-4 bg-white rounded-full animate-pulse" style={{animationDelay: '150ms'}} />
                      <div className="w-0.5 sm:w-1 h-1.5 sm:h-2 bg-white rounded-full animate-pulse" style={{animationDelay: '300ms'}} />
                    </div>
                    <span className="text-white text-[10px] sm:text-xs font-medium">LIVE</span>
                  </div>
                )}
                
                {/* Content - Compact */}
                <div className="relative p-2 sm:p-3 lg:p-4">
                  {/* Station Icon & Name */}
                  <div className="text-center mb-1.5 sm:mb-2">
                    <div className="text-2xl sm:text-3xl lg:text-4xl mb-1">{station.icon}</div>
                    <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white leading-tight">
                      {station.name}
                    </h3>
                    <p className="text-white/70 font-medium text-[10px] sm:text-xs line-clamp-1">
                      {station.tagline}
                    </p>
                  </div>
                  
                  {/* Now Playing for this station - Compact */}
                  {isStationPlaying && nowPlaying[station.id] && (
                    <div className="bg-black/30 backdrop-blur-sm rounded-md p-1.5 sm:p-2 mb-2">
                      <p className="text-white/60 text-[10px] mb-0.5">Τώρα:</p>
                      <p className="text-white text-[10px] sm:text-xs font-medium truncate">
                        {nowPlaying[station.id].fullText}
                      </p>
                    </div>
                  )}
                  
                  {/* Description - Hidden on mobile */}
                  <p className="text-white/50 text-[10px] sm:text-xs mb-2 hidden lg:block line-clamp-2">
                    {station.description}
                  </p>
                  
                  {/* Play Button - Compact */}
                  <button
                    onClick={() => handlePlayPause(station)}
                    disabled={isLoading && currentStation === station.id}
                    className={`
                      w-full flex items-center justify-center gap-1 sm:gap-2 py-2 sm:py-2.5 rounded-lg
                      font-bold text-xs sm:text-sm transition-all duration-300
                      ${isStationPlaying 
                        ? 'bg-white text-gray-900 hover:bg-gray-100' 
                        : 'bg-white/20 text-white hover:bg-white/30'}
                      ${isLoading && currentStation === station.id ? 'opacity-70 cursor-wait' : ''}
                    `}
                    data-testid={`play-btn-${station.id}`}
                  >
                    {isLoading && currentStation === station.id ? (
                      <>
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        <span className="hidden sm:inline">Φόρτωση...</span>
                      </>
                    ) : isStationPlaying ? (
                      <>
                        <Pause className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>Παύση</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 sm:w-5 sm:h-5 fill-current" />
                        <span className="hidden sm:inline">Ακούστε</span>
                        <span className="sm:hidden">Play</span>
                      </>
                    )}
                  </button>
                </div>
                
                {/* Decorative elements - smaller */}
                <div className="absolute top-0 right-0 w-16 sm:w-20 lg:w-24 h-16 sm:h-20 lg:h-24 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              </div>
            );
          })}
        </div>

        {/* Mobile Volume Control - Only visible on mobile when playing */}
        {currentStation && isPlaying && (
          <div className="sm:hidden fixed bottom-16 left-0 right-0 z-40 px-4 pb-2">
            <div className="bg-black/60 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-3">
              <button onClick={toggleMute} className="text-white/80">
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="flex-1 accent-purple-500"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OurStations;
